/**
 * Action Center API service.
 *
 * Reads the caller's inbox through the Gateway `ListNotifications` / `UpdateNotificationStatus`
 * proxies, which forward to JwoNotificationService. Both operations are user-self-scoped: the
 * subject is the forwarded caller identity, so no userId is sent.
 */

import { getServiceClient } from '@api/client-factory'
import { ListNotificationsInput } from '@amzn/coral_com-amazon-jwonotificationservice/ListNotificationsInput'
import { Notification } from '@amzn/coral_com-amazon-jwonotificationservice/Notification'
import { NotificationStatus } from '@amzn/coral_com-amazon-jwonotificationservice/NotificationStatus'
import { UpdateNotificationStatusInput } from '@amzn/coral_com-amazon-jwonotificationservice/UpdateNotificationStatusInput'
import { ActionCenterPriority, IActionCenterNotification } from '../types'
import { IMMEDIATE_ATTENTION_PRIORITIES, LIST_MAX_ITEMS, LIST_MAX_PAGES, LIST_PAGE_SIZE } from '../constants'
import { toActionCenterNotification } from '../utils'

/**
 * The service serves three reads behind one operation, keyed on the request fields: with no
 * `storeId` and `status=UNREAD`, the caller's *unread* items across every store (and store-less
 * ones); with a `storeId` alone, that store's *full history* (read and unread); with a `storeId`
 * AND `status=UNREAD`, that store's *unread* items only (server-filtered). The Action Center uses
 * all three.
 */
type ListScope =
    | { kind: 'all-unread' }
    | { kind: 'store-history', storeId: string }
    | { kind: 'store-unread', storeId: string }

/** Result of one inbox read. */
export interface IInboxRead {
    items: IActionCenterNotification[]
    /**
     * True when the scope held more than LIST_MAX_ITEMS. `items` is then the newest
     * LIST_MAX_ITEMS of the scope plus every CRITICAL and HIGH item in it (fetched through their
     * own priority-filtered reads), so only MEDIUM and LOW items can be missing.
     */
    truncated: boolean
}

/** Result of the cheap single-page unread count that feeds the bell badge. */
export interface IUnreadCount {
    /** Unread items on the first page; exact when `hasMore` is false. */
    count: number
    /** The inbox has more unread items than one page holds. */
    hasMore: boolean
}

interface IDrainResult {
    items: IActionCenterNotification[]
    truncated: boolean
}

export class ActionCenterAPIService {
    /**
     * Reads the caller's unread inbox across all their stores. Feeds the "All stores" tab and,
     * once the sheet is open, the exact bell count.
     *
     * Items come back with whatever `storeId` the producer wrote (the Gateway only rewrites
     * store ids when the request itself carried one), so they are treated as view-only.
     */
    public static listUnreadNotifications (): Promise<IInboxRead> {
        return ActionCenterAPIService.read({ kind: 'all-unread' })
    }

    /**
     * Reads one store's notifications, read and unread. Feeds the "Current store" tab.
     *
     * `storeId` is the platform (universal) store id. Because the request carries it, the
     * Gateway translates it for the downstream call and rewrites each item's `storeId` back to
     * the same id, so these items compare equal to the current store.
     */
    public static listStoreNotifications (storeId: string): Promise<IInboxRead> {
        return ActionCenterAPIService.read({ kind: 'store-history', storeId })
    }

    /**
     * Reads one store's UNREAD items only, server-filtered. Feeds the "All stores" fan-out, where
     * a store's read history is irrelevant and must not crowd its unread items out of the page
     * cap. The request carries BOTH the platform `storeId` (so the Gateway translates it and each
     * item comes back with the platform id, giving exact scoping) AND `status=UNREAD` (so the cap
     * and `truncated` apply to unread items, not read history).
     */
    public static listStoreUnreadNotifications (storeId: string): Promise<IInboxRead> {
        return ActionCenterAPIService.read({ kind: 'store-unread', storeId })
    }

    /**
     * Counts unread items with a single page, for the bell badge. The service has no count
     * operation reachable from here, and draining the whole inbox just to take its length is
     * the wrong price for a badge: one page is exact for small inboxes and "more than a page"
     * is all the badge needs to say for large ones.
     */
    public static async countUnreadNotifications (): Promise<IUnreadCount> {
        const output = await getServiceClient()
            .listNotifications(ActionCenterAPIService.input({ kind: 'all-unread' }, undefined, undefined))
            .toPromise()
        return {
            count: ActionCenterAPIService.render(output.items ?? []).length,
            hasMore: !!output.nextToken,
        }
    }

    /** Marks a notification READ. Terminal for the inbox: it drops out of the unread feed. */
    public static async acknowledge (notificationId: string): Promise<void> {
        const input = UpdateNotificationStatusInput.clone({
            notificationId,
            status: NotificationStatus.READ,
        })
        await getServiceClient().updateNotificationStatus(input).toPromise()
    }

    /**
     * Drains the scope, then closes the one hole the page cap opens. The service pages
     * newest-first with no priority ordering, so a drain that stops at LIST_MAX_PAGES drops the
     * *oldest* items regardless of priority, and an old CRITICAL would silently vanish from
     * "Needs immediate attention". When (and only when) the general drain was cut short, each
     * immediate-attention priority is re-read with the service's `priority` filter, normally one
     * page each, and merged in, so the immediate section is complete and the cap only affects
     * the MEDIUM/LOW accordion, where "the newest LIST_MAX_ITEMS" is an honest meaning.
     */
    private static async read (scope: ListScope): Promise<IInboxRead> {
        const general = await ActionCenterAPIService.drain(scope)
        if (!general.truncated) {
            return { items: general.items, truncated: false }
        }
        const topUps = await Promise.all(
            IMMEDIATE_ATTENTION_PRIORITIES.map((priority) => ActionCenterAPIService.drain(scope, priority)),
        )
        const byId = new Map<string, IActionCenterNotification>()
        for (const item of general.items) byId.set(item.notificationId, item)
        for (const topUp of topUps) {
            for (const item of topUp.items) {
                if (!byId.has(item.notificationId)) byId.set(item.notificationId, item)
            }
        }
        return { items: [...byId.values()], truncated: true }
    }

    /**
     * Follows `nextToken` until the service runs out or LIST_MAX_PAGES is reached. Every page is
     * fetched before rendering because the Action Center ranks by severity and counts per
     * category client-side, which needs the whole set.
     */
    private static async drain (scope: ListScope, priority?: ActionCenterPriority): Promise<IDrainResult> {
        const client = getServiceClient()
        const items: Notification[] = []
        let nextToken: string | undefined
        for (let page = 0; page < LIST_MAX_PAGES; page++) {
            const output = await client.listNotifications(ActionCenterAPIService.input(scope, priority, nextToken)).toPromise()
            items.push(...(output.items ?? []))
            nextToken = output.nextToken || undefined
            if (!nextToken) break
        }
        const truncated = !!nextToken
        if (truncated) {
            // Goes to RUM through WebLogInterceptor; a scope this large is worth knowing about.
            console.warn(`Action Center: ${describeScope(scope, priority)} exceeded ${LIST_MAX_ITEMS} items; showing the newest ${LIST_MAX_ITEMS}`)
        }
        return { items: ActionCenterAPIService.render(items), truncated }
    }

    private static input (scope: ListScope, priority: ActionCenterPriority | undefined, nextToken: string | undefined): ListNotificationsInput {
        return ListNotificationsInput.clone({
            ...(scope.kind === 'all-unread'
                ? { status: NotificationStatus.UNREAD }
                : scope.kind === 'store-unread'
                    ? { storeId: scope.storeId, status: NotificationStatus.UNREAD }
                    : { storeId: scope.storeId }),
            ...(priority ? { priority } : {}),
            maxResults: LIST_PAGE_SIZE,
            nextToken,
        })
    }

    private static render (items: Notification[]): IActionCenterNotification[] {
        return items
            .map(toActionCenterNotification)
            .filter((n): n is IActionCenterNotification => n !== null)
    }
}

const describeScope = (scope: ListScope, priority?: ActionCenterPriority): string => {
    const base = scope.kind === 'all-unread'
        ? 'unread inbox'
        : scope.kind === 'store-unread'
            ? `store ${scope.storeId} unread`
            : `store ${scope.storeId} history`
    return `${base}${priority ? ` (${priority})` : ''}`
}
